import {
  THREAD_DELIVERY_STATE_EXTENSION_URL,
  THREAD_DELIVERY_STATE_LAST_DELIVERED_AT_URL,
  THREAD_DELIVERY_STATE_PARTICIPANT_URL,
} from '@/model/middleware-types'
import { SNOMED_USER_ROLES } from '@/utils'
import { logError } from '@/utils/errorUtils'
import {
  getReferenceString,
  getStatus,
  OperationOutcomeError,
  type MedplumClient,
} from '@medplum/core'
import type {
  Bundle,
  Communication,
  Extension,
  Practitioner,
  PractitionerRole,
  Patient,
} from '@medplum/fhirtypes'

export type MessageThreadParticipant = Pick<Practitioner, 'id' | 'name' | 'resourceType'>
export type MiddlewareThreadSummary = MessageThreadSummary & {
  latestMessage?: Communication
  status: ThreadStatus
  /** Unread messages for the current practitioner; see ThreadListItem in middleware. */
  unreadCount?: number
}
type CreateMessageThreadArgs = {
  creator: MessageThreadParticipant
  recipient: MessageThreadParticipant
  subject: string
  initialMessage: string
  isMarkedAsUrgent?: boolean
  isMarkedAsFollowUp?: boolean
  medplum: MedplumClient
}

type CreateThreadMessageArgs = {
  thread: Communication
  sender: MessageThreadParticipant
  recipients: MessageThreadParticipant[]
  message: string
  medplum: MedplumClient
}

type CreateThreadFileMessageArgs = {
  thread: Communication
  sender: MessageThreadParticipant
  recipients: MessageThreadParticipant[]
  file: File
  medplum: MedplumClient
}

export type MessageThreadSummary = {
  thread: Communication
  sender?: PractitionerThreadOption
  recipients: PractitionerThreadOption[]
  relatedPatient: Patient | null
}

export type ThreadMessage = {
  message: Communication
  sender?: Practitioner
  recipients: Practitioner[]
}

export type PractitionerThreadOption = {
  practitioner: Practitioner
  accessPolicyName: 'schedule-manager-access' | 'call-center-agent-access' | 'practitioner-access'
}

export type ThreadStatus = 'read' | 'unread'

type UpdateThreadReadStateArgs = {
  medplum: MedplumClient
  thread: Communication
  participant: MessageThreadParticipant
  lastReadMessage: Communication
}

const THREAD_READ_STATE_EXTENSION_URL =
  'https://behuman.health/fhir/StructureDefinition/thread-read-state'
const THREAD_READ_STATE_PARTICIPANT_URL = 'participant'
const THREAD_READ_STATE_LAST_READ_URL = 'lastRead'
const THREAD_READ_STATE_LAST_READ_AT_URL = 'lastReadAt'
export const THREAD_LABELS_EXTENSION_URL =
  'https://behuman.health/fhir/StructureDefinition/thread-labels'
export const THREAD_LABEL_ITEM_URL = 'label'
export const LEGACY_THREAD_LABEL_EXTENSION_URL =
  'https://behuman.health/fhir/StructureDefinition/thread-label'
export const THREAD_LABEL_URGENT = 'urgent'
export const THREAD_LABEL_FOLLOW_UP = 'follow-up'

export const ACCESS_POLICY_BADGE_LABELS: Record<
  PractitionerThreadOption['accessPolicyName'],
  string
> = {
  'schedule-manager-access': 'Schedule manager',
  'call-center-agent-access': 'CC agent',
  'practitioner-access': 'Doctor',
}

function getThreadReadStateReference(
  extension: Extension | undefined,
  url: string,
): string | undefined {
  return extension?.extension?.find((item) => item.url === url)?.valueReference?.reference
}

function getThreadReadStateDateTime(
  extension: Extension | undefined,
  url: string,
): string | undefined {
  return extension?.extension?.find((item) => item.url === url)?.valueDateTime
}

export function getCommunicationFromSubscriptionBundle(bundle: Bundle): Communication | undefined {
  return bundle.entry?.find((entry) => entry.resource?.resourceType === 'Communication')
    ?.resource as Communication | undefined
}

export function isThreadHeaderCommunication(communication: Communication | undefined): boolean {
  return Boolean(communication && (!communication.partOf || communication.partOf.length === 0))
}

export function isArchivedThread(thread: Communication): boolean {
  return thread.status === 'completed'
}

export function getThreadLabels(thread: Communication): string[] {
  const nestedLabels =
    thread.extension
      ?.filter((extension) => extension.url === THREAD_LABELS_EXTENSION_URL)
      .flatMap(
        (extension) =>
          extension.extension
            ?.filter(
              (item) => item.url === THREAD_LABEL_ITEM_URL && typeof item.valueString === 'string',
            )
            .map((item) => item.valueString as string) ?? [],
      ) ?? []

  const legacyLabels =
    thread.extension
      ?.filter(
        (extension) =>
          extension.url === LEGACY_THREAD_LABEL_EXTENSION_URL &&
          typeof extension.valueString === 'string',
      )
      .map((extension) => extension.valueString as string) ?? []

  return [...new Set([...nestedLabels, ...legacyLabels])]
}

function getAccessPolicyNameFromRole(
  role: PractitionerRole | undefined,
): PractitionerThreadOption['accessPolicyName'] {
  const roleCodes = role?.code?.flatMap((codeableConcept) =>
    (codeableConcept.coding ?? []).map((coding) => coding.code),
  )

  if (roleCodes?.includes('303129000')) {
    return 'schedule-manager-access'
  }

  if (roleCodes?.includes('224565004')) {
    return 'call-center-agent-access'
  }

  return 'practitioner-access'
}

function getPractitionerIdFromReference(reference: string | undefined): string | undefined {
  if (!reference?.startsWith('Practitioner/')) {
    return undefined
  }

  return reference.replace('Practitioner/', '')
}

function getCommunicationIdFromReference(reference: string | undefined): string | undefined {
  if (!reference?.startsWith('Communication/')) {
    return undefined
  }

  return reference.replace('Communication/', '')
}

function getThreadReadStateExtension(
  thread: Communication,
  practitionerId: string,
): Extension | undefined {
  return thread.extension?.find(
    (extension) =>
      extension.url === THREAD_READ_STATE_EXTENSION_URL &&
      getPractitionerIdFromReference(
        getThreadReadStateReference(extension, THREAD_READ_STATE_PARTICIPANT_URL),
      ) === practitionerId,
  )
}

function buildThreadReadStateExtension(
  participant: MessageThreadParticipant,
  lastReadMessage: Communication,
): Extension {
  return {
    url: THREAD_READ_STATE_EXTENSION_URL,
    extension: [
      {
        url: THREAD_READ_STATE_PARTICIPANT_URL,
        valueReference: {
          reference: getReferenceString(participant),
        },
      },
      {
        url: THREAD_READ_STATE_LAST_READ_URL,
        valueReference: {
          reference: getReferenceString(lastReadMessage),
        },
      },
      {
        url: THREAD_READ_STATE_LAST_READ_AT_URL,
        valueDateTime: lastReadMessage.sent,
      },
    ],
  }
}

export function checkThreadStatus(
  thread: Communication,
  practitionerId: string,
  latestMessage?: Communication,
): ThreadStatus {
  const readState = getThreadReadStateExtension(thread, practitionerId)

  if (!readState || !latestMessage?.id) {
    return 'unread'
  }

  const lastReadMessageId = getCommunicationIdFromReference(
    getThreadReadStateReference(readState, THREAD_READ_STATE_LAST_READ_URL),
  )
  const lastReadAt = getThreadReadStateDateTime(readState, THREAD_READ_STATE_LAST_READ_AT_URL)

  if (lastReadMessageId === latestMessage.id) {
    return 'read'
  }

  if (lastReadAt && latestMessage.sent && lastReadAt >= latestMessage.sent) {
    return 'read'
  }

  return 'unread'
}

/**
 * When `practitionerId` last read this thread, as the `sent` timestamp of the
 * newest message they had seen. Undefined if they have never opened it.
 *
 * Read receipts on outgoing messages are derived from this: the participant has
 * read message M when their `lastReadAt` is at or after `M.sent`. No per-message
 * state is stored — this extension is written by `POST /threads/read` and so is
 * already durable across reloads, devices, and sessions.
 */
export function getParticipantLastReadAt(
  thread: Communication,
  practitionerId: string,
): string | undefined {
  const readState = getThreadReadStateExtension(thread, practitionerId)
  return readState
    ? getThreadReadStateDateTime(readState, THREAD_READ_STATE_LAST_READ_AT_URL)
    : undefined
}

/**
 * When `practitionerId`'s client last fetched a message in this thread, as that
 * message's `sent` timestamp. Undefined until they have acknowledged anything.
 *
 * Delivery receipts are derived from this the same way read receipts are
 * derived from `lastReadAt` — no per-message state, and durable because it
 * lives on the thread header.
 */
export function getParticipantLastDeliveredAt(
  thread: Communication,
  practitionerId: string,
): string | undefined {
  const deliveryState = thread.extension?.find(
    (extension) =>
      extension.url === THREAD_DELIVERY_STATE_EXTENSION_URL &&
      getPractitionerIdFromReference(
        getThreadReadStateReference(extension, THREAD_DELIVERY_STATE_PARTICIPANT_URL),
      ) === practitionerId,
  )

  return deliveryState
    ? getThreadReadStateDateTime(deliveryState, THREAD_DELIVERY_STATE_LAST_DELIVERED_AT_URL)
    : undefined
}

const THREAD_WRITE_ATTEMPTS = 3

/** A 412 from a versioned write — the thread changed after we read it. */
function isPreconditionFailed(error: unknown): boolean {
  return error instanceof OperationOutcomeError && getStatus(error.outcome) === 412
}

/**
 * Apply a change to a thread header without clobbering concurrent updates.
 *
 * Callers hold a snapshot of the thread from when the conversation was opened,
 * which can be minutes stale: the other participant may have archived it or
 * advanced their read/delivery pointer since. Writing `{...snapshot, change}`
 * silently reverts all of that — that is how sending a message used to
 * resurrect a thread the other participant had archived.
 *
 * So always re-read, apply the change to the fresh copy, and guard the write
 * with the version it was based on.
 */
async function updateThreadResource(
  medplum: MedplumClient,
  threadId: string,
  /**
   * Called with the freshly read thread on every attempt, so a retry re-decides
   * against current state rather than replaying a stale edit. Return `null` to
   * abandon the write — used when the thread already reflects the change, or has
   * moved past it and writing would roll it back.
   */
  apply: (thread: Communication) => Communication | null,
): Promise<Communication> {
  let current = await medplum.readResource('Communication', threadId)

  for (let attempt = 0; attempt < THREAD_WRITE_ATTEMPTS; attempt++) {
    const next = apply(current)
    if (!next) {
      return current
    }

    try {
      return await medplum.updateResource<Communication>(next, {
        headers: { 'If-Match': `W/"${current.meta?.versionId ?? ''}"` },
      })
    } catch (error) {
      if (!isPreconditionFailed(error) || attempt === THREAD_WRITE_ATTEMPTS - 1) {
        throw error
      }

      current = await medplum.readResource('Communication', threadId)
    }
  }

  throw new Error('Unable to update the thread after repeated conflicting updates.')
}

export async function updateThreadReadState({
  medplum,
  thread,
  participant,
  lastReadMessage,
}: UpdateThreadReadStateArgs): Promise<Communication> {
  if (!thread.id || !participant.id || !lastReadMessage.id || !lastReadMessage.sent) {
    throw new Error(
      'Thread, participant, and latest read message are required to update thread read state.',
    )
  }

  const nextReadStateExtension = buildThreadReadStateExtension(participant, lastReadMessage)
  const participantId = participant.id
  const nextReadAt = lastReadMessage.sent

  return updateThreadResource(medplum, thread.id, (current) => {
    const existing = current.extension?.find(
      (extension) =>
        extension.url === THREAD_READ_STATE_EXTENSION_URL &&
        getPractitionerIdFromReference(
          getThreadReadStateReference(extension, THREAD_READ_STATE_PARTICIPANT_URL),
        ) === participantId,
    )

    // The pointer only ever moves forward. Two reads of the same thread can
    // overlap — if the older one retries after losing the version race, writing
    // its timestamp would mark already-read messages unread again and downgrade
    // the sender's receipt.
    const existingReadAt = getThreadReadStateDateTime(existing, THREAD_READ_STATE_LAST_READ_AT_URL)
    if (existingReadAt && existingReadAt >= nextReadAt) {
      return null
    }

    return {
      ...current,
      extension: [
        ...(current.extension?.filter((extension) => extension !== existing) ?? []),
        nextReadStateExtension,
      ],
    }
  })
}

export async function getOrganizationPractitioners(
  medplum: MedplumClient,
  currentPractitionerId: string,
): Promise<PractitionerThreadOption[]> {
  const currentRole = await medplum.searchOne(
    'PractitionerRole',
    `practitioner=Practitioner/${currentPractitionerId}`,
  )

  if (!currentRole?.organization?.reference) {
    throw new Error('Unable to resolve the current practitioner organization.')
  }

  const [organizationBundle, callCenterBundle] = await Promise.all([
    medplum.search('PractitionerRole', {
      organization: currentRole.organization.reference,
      _include: 'PractitionerRole:practitioner',
      active: 'true',
      _count: 500,
    }),
    medplum.search('PractitionerRole', {
      role: SNOMED_USER_ROLES.CC_MANAGER.code,
      _include: 'PractitionerRole:practitioner',
      active: 'true',
      _count: 500,
    }),
  ])

  const practitioners = new Map<string, Practitioner>()
  const practitionerRoles = new Map<string, PractitionerRole>()

  for (const bundle of [organizationBundle, callCenterBundle]) {
    for (const entry of bundle.entry ?? []) {
      const resource = entry.resource as Practitioner | PractitionerRole | undefined
      if (resource?.resourceType === 'Practitioner' && resource.id) {
        practitioners.set(resource.id, resource)
      }
      if (resource?.resourceType === 'PractitionerRole') {
        const practitionerId = resource.practitioner?.reference?.replace('Practitioner/', '')
        if (practitionerId) {
          practitionerRoles.set(practitionerId, resource)
        }
      }
    }
  }

  return Array.from(practitioners.values())
    .filter((practitioner) => practitioner.id !== currentPractitionerId)
    .map((practitioner) => ({
      practitioner,
      accessPolicyName: getAccessPolicyNameFromRole(
        practitionerRoles.get(practitioner.id as string),
      ),
    }))
}

export async function createMessageThread({
  creator,
  recipient,
  subject,
  initialMessage,
  isMarkedAsUrgent,
  isMarkedAsFollowUp,
  medplum,
}: CreateMessageThreadArgs): Promise<{ thread: Communication; initialMessage: Communication }> {
  if (!creator.id || !recipient.id) {
    throw new Error('Both practitioners must have valid identifiers.')
  }

  const sent = new Date().toISOString()
  const threadLabels = [
    ...(isMarkedAsUrgent ? [THREAD_LABEL_URGENT] : []),
    ...(isMarkedAsFollowUp ? [THREAD_LABEL_FOLLOW_UP] : []),
  ]

  const thread = await medplum.createResource<Communication>({
    resourceType: 'Communication',
    status: 'in-progress',
    category: [
      {
        coding: [
          {
            system: 'http://behuman.com/fhir/communication-category',
            code: 'practitioner-message',
            display: 'Practitioner Message',
          },
        ],
      },
    ],
    sent,
    sender: {
      reference: getReferenceString(creator),
    },
    recipient: [
      {
        reference: getReferenceString(recipient),
      },
    ],
    topic: {
      text: subject,
    },
    extension:
      threadLabels.length > 0
        ? [
            {
              url: THREAD_LABELS_EXTENSION_URL,
              extension: threadLabels.map((label) => ({
                url: THREAD_LABEL_ITEM_URL,
                valueString: label,
              })),
            },
          ]
        : undefined,
  })

  let firstMessage: Communication
  try {
    firstMessage = await medplum.createResource<Communication>({
      resourceType: 'Communication',
      status: 'completed',
      sent,
      sender: {
        reference: getReferenceString(creator),
      },
      recipient: [
        {
          reference: getReferenceString(recipient),
        },
      ],
      topic: {
        text: subject,
      },
      partOf: [
        {
          reference: getReferenceString(thread),
        },
      ],
      payload: [
        {
          contentString: initialMessage,
        },
      ],
    })
  } catch (error) {
    try {
      if (thread.id) {
        await medplum.deleteResource('Communication', thread.id)
      }
    } catch (cleanupError) {
      logError('messages', `Failed to roll back orphaned thread ${thread.id}`, cleanupError)
    }

    throw error
  }

  return {
    thread,
    initialMessage: firstMessage,
  }
}

export async function createThreadMessage({
  thread,
  sender,
  recipients,
  message,
  medplum,
}: CreateThreadMessageArgs): Promise<Communication> {
  if (!thread.id || !sender.id || recipients.length === 0) {
    throw new Error('Thread, sender, and recipients are required to create a message.')
  }

  const sent = new Date().toISOString()

  const createdMessage = await medplum.createResource<Communication>({
    resourceType: 'Communication',
    status: 'completed',
    sent,
    sender: {
      reference: getReferenceString(sender),
    },
    recipient: recipients
      .filter((recipient) => Boolean(recipient.id))
      .map((recipient) => ({
        reference: getReferenceString(recipient),
      })),
    topic: {
      text: thread.topic?.text || '',
    },
    partOf: [
      {
        reference: getReferenceString(thread),
      },
    ],
    payload: [
      {
        contentString: message,
      },
    ],
  })

  await updateThreadReadState({
    medplum,
    thread,
    participant: sender,
    lastReadMessage: createdMessage,
  })

  return createdMessage
}

export async function createThreadFileMessage({
  thread,
  sender,
  recipients,
  file,
  medplum,
}: CreateThreadFileMessageArgs): Promise<Communication> {
  if (!thread.id || !sender.id || recipients.length === 0) {
    throw new Error('Thread, sender, and recipients are required to upload a file.')
  }

  const sent = new Date().toISOString()

  const createdMessage = await medplum.createResource<Communication>({
    resourceType: 'Communication',
    status: 'completed',
    sent,
    sender: {
      reference: getReferenceString(sender),
    },
    recipient: recipients
      .filter((recipient) => Boolean(recipient.id))
      .map((recipient) => ({
        reference: getReferenceString(recipient),
      })),
    topic: {
      text: thread.topic?.text || '',
    },
    partOf: [
      {
        reference: getReferenceString(thread),
      },
    ],
  })

  const binary = await medplum.createBinary({
    data: file,
    filename: file.name,
    contentType: file.type || 'application/octet-stream',
    securityContext: {
      reference: getReferenceString(createdMessage),
    },
  })
  const binaryUrl = binary.url ?? `${medplum.getBaseUrl()}Binary/${binary.id}`

  const updatedMessage = await medplum.updateResource<Communication>({
    ...createdMessage,
    payload: [
      {
        contentAttachment: {
          contentType: file.type || 'application/octet-stream',
          title: file.name,
          url: binaryUrl,
        },
      },
    ],
  })

  await updateThreadReadState({
    medplum,
    thread,
    participant: sender,
    lastReadMessage: updatedMessage,
  })

  return updatedMessage
}

export async function updateThreadArchivedState(
  medplum: MedplumClient,
  thread: Communication,
  archived: boolean,
): Promise<Communication> {
  if (!thread.id) {
    throw new Error('Thread identifier is required to update archive state.')
  }

  // Archiving from a stale snapshot would roll back the other participant's
  // read and delivery pointers, so it goes through the same versioned write.
  const nextStatus = archived ? 'completed' : 'in-progress'

  return updateThreadResource(medplum, thread.id, (current) =>
    // Already in the requested state — skip the write rather than bump the
    // version and risk losing a concurrent pointer update for nothing.
    current.status === nextStatus ? null : { ...current, status: nextStatus },
  )
}
